import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, User, Icon } from 'lucide-react';
import { horseHead } from '@lucide/lab';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { trackSearch, getPostHog } from '@/hooks/usePostHogTracking';

interface SearchSuggestion {
  type: 'rider' | 'horse';
  name: string;
  riderName?: string;
  swissEquestrianRiderId: string | null;
  swissEquestrianHorseId?: string | null;
  isExternal?: boolean;
}

export const SearchBox: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const fetchSuggestions = async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('search-autocomplete', {
        body: { query: searchQuery }
      });
      if (error) { console.error('Autocomplete error:', error); return; }
      if (data && Array.isArray(data)) setSuggestions(data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => fetchSuggestions(query), 300);
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [query]);

  const saveRecentSearch = (searchQuery: string) => {
    try {
      const saved = localStorage.getItem('recent_searches');
      let searches = saved ? JSON.parse(saved) : [];
      searches = searches.filter((search: any) => search.query !== searchQuery);
      searches.unshift({ query: searchQuery, timestamp: Date.now() });
      searches = searches.slice(0, 10);
      localStorage.setItem('recent_searches', JSON.stringify(searches));
    } catch (error) {
      console.error('Error saving recent search:', error);
    }
  };

  const handleSearch = (searchQuery?: string) => {
    const finalQuery = searchQuery || query;
    if (finalQuery.trim()) {
      const cleanQuery = finalQuery.trim();
      trackSearch(getPostHog(), cleanQuery, 'query', {
        suggestions_shown: suggestions.length,
        from_suggestion: !!searchQuery
      });
      saveRecentSearch(cleanQuery);
      const encodedQuery = encodeURIComponent(cleanQuery.replace(/\s+/g, '+'));
      navigate(`/search/q/${encodedQuery}`);
      setShowSuggestions(false);
      setQuery('');
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    const ph = getPostHog();
    
    // Track autocomplete suggestion selection
    if (ph) {
      ph.capture('autocomplete_suggestion_selected', {
        suggestion_type: suggestion.type,
        suggestion_name: suggestion.name,
        rider_id: suggestion.swissEquestrianRiderId,
        horse_id: suggestion.swissEquestrianHorseId || null,
        rider_name: suggestion.riderName || null,
        is_external: suggestion.isExternal || false,
      });
    }

    setShowSuggestions(false);
    setQuery('');

    const doNavigate = (path: string) => navigate(path);

    if (suggestion.type === 'rider' && suggestion.swissEquestrianRiderId) {
      trackSearch(ph, suggestion.name, 'rider', {
        rider_id: suggestion.swissEquestrianRiderId,
        from_suggestion: true
      });
      saveRecentSearch(suggestion.name);
      doNavigate(`/search/rider/${suggestion.swissEquestrianRiderId}`);
    } else if (suggestion.type === 'horse') {
      trackSearch(ph, suggestion.name, 'horse', {
        horse_id: suggestion.swissEquestrianHorseId,
        from_suggestion: true
      });
      saveRecentSearch(suggestion.name);
      const encodedQuery = encodeURIComponent(suggestion.name.trim().replace(/\s+/g, '+'));
      doNavigate(`/search/q/${encodedQuery}`);
    } else {
      trackSearch(ph, suggestion.name, 'query', { from_suggestion: true });
      saveRecentSearch(suggestion.name);
      const encodedQuery = encodeURIComponent(suggestion.name.trim().replace(/\s+/g, '+'));
      doNavigate(`/search/q/${encodedQuery}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
    else if (e.key === 'Escape') setShowSuggestions(false);
  };

  return (
    <div className="relative w-full">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={t('search.placeholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          className="pl-12 pr-24 h-14 text-base text-on-surface placeholder:text-muted-foreground rounded-md"
        />
        <div className="absolute right-1.5 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {query && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setQuery(''); setSuggestions([]); inputRef.current?.focus(); }}
              className="h-8 w-8 p-0"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => handleSearch()}
            disabled={!query.trim()}
            className="h-10 px-4 gradient-editorial text-white"
          >
            {t('search.searchButton')}
          </Button>
        </div>
      </div>

      {showSuggestions && (suggestions.length > 0 || isLoading) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-surface-container-lowest rounded-md shadow-float z-[99999] max-h-72 overflow-hidden">
          <div className="max-h-72 overflow-y-auto">
            {isLoading && (
              <div className="p-4 text-sm text-muted-foreground text-center">
                {t('common.loading')}
              </div>
            )}
            {!isLoading && suggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.type}-${suggestion.name}-${index}`}
                className="w-full text-left p-3.5 hover:bg-surface-container-low transition-colors text-on-surface"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-surface-container-high text-muted-foreground">
                    {suggestion.type === 'rider' ? (
                      <User className="w-3.5 h-3.5" />
                    ) : (
                      <Icon iconNode={horseHead} size={14} />
                    )}
                  </div>
                  <span className="font-medium text-sm">{suggestion.name}</span>
                </div>
              </button>
            ))}
          </div>
          {!isLoading && suggestions.length > 4 && (
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-surface-container-lowest to-transparent pointer-events-none rounded-b-md" />
          )}
        </div>
      )}
    </div>
  );
};
